import { LayoutGrid, Zap, FileText, MessageSquare, User, Edit, Sparkles, LogOut, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import Link from "next/link";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { quickAccessLinks } from "@/lib/nav-data";
import { CreateProfileDialog } from "../create-profile-dialog";
import { useEffect, useState } from "react";
import { Badge } from "../ui/badge";
import { NameAvatar } from "../ui/name-avatar";
import { useIsMobile } from "@/hooks/use-mobile";

export const MainMenu = () => {
    const { isLoggedIn, role, applicationCount, savedJobCount, lastAction, user } = useAuth();
    const [totalNotificationCount, setTotalNotificationCount] = useState(0);
    const [myJobsLink, setMyJobsLink] = useState('/viec-lam-cua-toi');
    const isEditing = role === 'candidate'
    const createProfileButtonText = isEditing ? 'Sửa hồ sơ' : 'Tạo hồ sơ';
    const isMobile = useIsMobile();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dialogStep, setDialogStep] = useState(1);
    const [dialogMode, setDialogMode] = useState<'quick' | 'detailed' | null>(null);

    const handleOpenQuick = () => {
        setDialogStep(2);
        setDialogMode('quick');
        setIsDialogOpen(true);
    };

    const handleOpenDetailed = () => {
        setDialogStep(1);
        setDialogMode('detailed');
        setIsDialogOpen(true);
    };

    useEffect(() => {
        setTotalNotificationCount(applicationCount + savedJobCount);
    }, [applicationCount, savedJobCount]);
    useEffect(() => {
        setMyJobsLink('/viec-lam-cua-toi' + (lastAction ? `?highlight=${lastAction}` : ''))
    }, [lastAction]);
    return <>

        {isLoggedIn ? (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="rounded-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 outline-none transition-transform hover:scale-105">
                        <NameAvatar src={user?.avatarUrl} fullName={user?.personalInfo?.fullName} size={35} />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 p-2 rounded-xl shadow-xl border-slate-100">
                    <div className="px-3 py-3 border-b mb-1">
                        <p className="text-sm font-bold text-slate-900 truncate">{user?.personalInfo?.fullName || 'Người dùng HelloJob'}</p>
                        <p className="text-xs text-slate-500 truncate">{user?.email || ''}</p>
                    </div>
                    
                    <DropdownMenuItem asChild className="focus:bg-[#0D8DC8]/10 rounded-lg">
                        <Link href="/ho-so-cua-toi" className="flex items-center gap-3 p-2.5 cursor-pointer font-medium text-[#0D8DC8]">
                            <div className="h-8 w-8 rounded-full bg-[#0D8DC8]/10 flex items-center justify-center">
                                <User className="h-4 w-4" />
                            </div>
                            <span className="flex-1">Xem hồ sơ</span>
                            <ChevronRight className="h-4 w-4 opacity-30" />
                        </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild className="focus:bg-[#F2B92A]/10 rounded-lg">
                        <Link href="/ho-so-cua-toi?mode=edit" className="flex items-center gap-3 p-2.5 cursor-pointer font-medium text-[#F2B92A]">
                            <div className="h-8 w-8 rounded-full bg-[#F2B92A]/10 flex items-center justify-center">
                                <Edit className="h-4 w-4" />
                            </div>
                            <span className="flex-1">Sửa hồ sơ</span>
                            <ChevronRight className="h-4 w-4 opacity-30" />
                        </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild className="focus:bg-[#AFC536]/10 rounded-lg">
                        <Link href="/tao-ho-so/tao-noi-dung-phong-van/ket-qua" className="flex items-center gap-3 p-2.5 cursor-pointer font-medium text-[#AFC536]">
                            <div className="h-8 w-8 rounded-full bg-[#AFC536]/10 flex items-center justify-center">
                                <Sparkles className="h-4 w-4" />
                            </div>
                            <span className="flex-1">Kết quả luyện phỏng vấn</span>
                            <ChevronRight className="h-4 w-4 opacity-30" />
                        </Link>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        ) : (
            <Button><Link href={'/xac-thuc'}>Đăng nhập / Đăng ký</Link></Button>
        )}

        {isMobile ? (
            <CreateProfileDialog>
                <Button className="bg-accent-orange hover:bg-accent-orange/90 text-white">{createProfileButtonText}</Button>
            </CreateProfileDialog>
        ) : (
            <>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button className="bg-accent-orange hover:bg-accent-orange/90 text-white">{createProfileButtonText}</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="w-72 p-2">
                        <DropdownMenuItem onClick={handleOpenQuick} className="cursor-pointer flex items-center gap-3 p-3 rounded-lg hover:bg-accent/10 transition-colors">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-orange/10">
                                <Zap className="h-5 w-5 text-accent-orange" />
                            </div>
                            <div className="flex flex-col">
                                <span className="font-bold text-sm text-foreground">Tạo nhanh</span>
                                <span className="text-xs text-muted-foreground">Gợi ý việc làm ngay lập tức</span>
                            </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleOpenDetailed} className="cursor-pointer flex items-center gap-3 p-3 rounded-lg hover:bg-accent/10 transition-colors">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-green/10">
                                <FileText className="h-5 w-5 text-accent-green" />
                            </div>
                            <div className="flex flex-col">
                                <span className="font-bold text-sm text-foreground">Tạo chi tiết</span>
                                <span className="text-xs text-muted-foreground">Hoàn thiện hồ sơ chuyên nghiệp</span>
                            </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="cursor-pointer">
                            <Link href="/tao-ho-so/tao-noi-dung-phong-van" className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent/10 transition-colors">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                                    <MessageSquare className="h-5 w-5 text-primary" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="font-bold text-sm text-foreground">Tạo nội dung phỏng vấn</span>
                                    <span className="text-xs text-muted-foreground">Chuẩn bị tốt nhất cho buổi phỏng vấn</span>
                                </div>
                            </Link>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                <CreateProfileDialog
                    open={isDialogOpen}
                    onOpenChange={setIsDialogOpen}
                    initialStep={dialogStep}
                    initialMode={dialogMode}
                />
            </>
        )}

        <Button asChild className="relative">
            <Link href={myJobsLink}>
                Việc của tôi
                {totalNotificationCount > 0 && (
                    <Badge className="absolute -top-2 -right-2 h-5 w-5 justify-center rounded-full bg-red-500 p-0 text-xs">
                        {totalNotificationCount > 9 ? '9+' : totalNotificationCount}
                    </Badge>
                )}
            </Link>
        </Button>
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                    <LayoutGrid className="h-5 w-5" />
                    <span className="sr-only">Open Menu</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[360px]" align="end" forceMount>
                {isLoggedIn ? (
                    <DropdownMenuItem asChild>
                        <Link
                            href="/ho-so-cua-toi"
                            className="block hover:bg-accent rounded-md p-2 cursor-pointer"
                        >
                            <div className="flex items-center gap-3">
                                <NameAvatar src={user?.avatarUrl} fullName={user?.personalInfo?.fullName} size={48} />
                                <div className="flex flex-col space-y-1 overflow-hidden">
                                    <p className="text-base font-medium leading-none truncate">{'Ứng viên'}</p>
                                    <p className="text-xs leading-none text-muted-foreground truncate">{'Cập nhật hồ sơ của bạn'}</p>
                                </div>
                            </div>
                        </Link>
                    </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                    <div className="grid grid-cols-4 gap-2 p-2">
                        {quickAccessLinks.map((link) => (
                            <DropdownMenuItem asChild key={link.href}>
                                <Link
                                    href={link.href}
                                    className="flex flex-col items-center justify-start p-2 h-20 cursor-pointer rounded-md hover:bg-accent"
                                >
                                    <div className="h-8 flex items-center justify-center">
                                        <link.icon />
                                    </div>
                                    <span className="text-xs text-center leading-tight">
                                        {link.label}
                                    </span>
                                </Link>
                            </DropdownMenuItem>
                        ))}
                    </div>
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    </>
};
